import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationHistory from '../components/NotificationHistory';
import LoginModal from '../components/LoginModal';
import { updateProfile } from '../api/client';
import './Profile.css';

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}


function getKeyBase(user) {
  if (!user) return null;
  if (user.username && user.username.trim()) return user.username.trim();
  return null;
}

function Profile() {
  const { user, isGuest, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    notifications: true,
  });


  const [savedFilters, setSavedFilters] = useState([]);
  const [defaultFilterId, setDefaultFilterId] = useState(null);

  const [showFilterForm, setShowFilterForm] = useState(false);
  const [filterForm, setFilterForm] = useState({
    name: '',
    period: '1M',
    volume: 'all',
    price: 'all',
  });


  const [filtersLoaded, setFiltersLoaded] = useState(false);

  useEffect(() => {
    if (isGuest) {
      setShowLoginModal(true);
    }
  }, [isGuest]);
  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      setProfileData({
        name: fullName || user.username || '',
        email: user.email || '',
        notifications: true,
      });
      if (user.avatar_url) {
        setProfileImage(user.avatar_url);
      }
    }
  }, [user]);


  useEffect(() => {
    const keyBase = getKeyBase(user);
    if (!keyBase) return;

    const filtersKey = `cv_saved_filters_${keyBase}`;
    const defaultKey = `cv_default_filter_id_${keyBase}`;


    setSavedFilters([]);
    setDefaultFilterId(null);
    setFiltersLoaded(false);

    const storedFilters = localStorage.getItem(filtersKey);
    if (storedFilters) {
      try {
        const parsed = JSON.parse(storedFilters);
        if (Array.isArray(parsed)) {
          setSavedFilters(parsed);
        }
      } catch (e) {
        console.error('Failed to parse saved filters from localStorage', e);
      }
    }

    const storedDefaultId = localStorage.getItem(defaultKey);
    if (storedDefaultId) {
      const parsedId = parseInt(storedDefaultId, 10);
      if (!Number.isNaN(parsedId)) {
        setDefaultFilterId(parsedId);
      }
    }


    setFiltersLoaded(true);
  }, [user]);


  useEffect(() => {
    const keyBase = getKeyBase(user);
    if (!keyBase || !filtersLoaded) return;

    const filtersKey = `cv_saved_filters_${keyBase}`;
    localStorage.setItem(filtersKey, JSON.stringify(savedFilters));
  }, [savedFilters, user, filtersLoaded]);


  useEffect(() => {
    const keyBase = getKeyBase(user);
    if (!keyBase || !filtersLoaded) return;

    const defaultKey = `cv_default_filter_id_${keyBase}`;

    if (defaultFilterId == null) {
      localStorage.removeItem(defaultKey);
      return;
    }

    localStorage.setItem(defaultKey, String(defaultFilterId));

    const selected = savedFilters.find((f) => f.id === defaultFilterId);
    if (selected) {
      localStorage.setItem(
        `cv_dashboard_filter_${keyBase}`,
        JSON.stringify(selected)
      );
    }
  }, [defaultFilterId, savedFilters, user, filtersLoaded]);


  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    // Parse name into first_name and last_name
    const nameParts = profileData.name.trim().split(/\s+/);
    const first_name = nameParts[0] || '';
    const last_name = nameParts.slice(1).join(' ') || '';
    
    // Validate email if provided
    if (profileData.email && !profileData.email.includes('@')) {
      alert('Ве молиме внесете валидна е-пошта адреса');
      return;
    }
    
    try {
      const updatedUser = await updateProfile({
        email: profileData.email || '',
        first_name: first_name,
        last_name: last_name,
      });
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      setIsEditing(false);
      alert('Профилот е успешно ажуриран!');
    } catch (err) {
      console.error('Failed to update profile:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Грешка при ажурирање на профилот';
      alert(`Грешка: ${errorMsg}`);
    }
  };


  const handleSaveFilter = () => {
    if (!filterForm.name.trim()) {
      alert('Внесете име за филтерот');
      return;
    }

    const newFilter = {
      id: Date.now(),
      ...filterForm,
      createdAt: new Date().toISOString(),
    };

    setSavedFilters((prev) => [...prev, newFilter]);

    setFilterForm({ name: '', period: '1M', volume: 'all', price: 'all' });
    setShowFilterForm(false);
  };

  const handleDeleteFilter = (id) => {
    if (!window.confirm('Дали сте сигурни дека сакате да го избришете овој филтер?')) {
      return;
    }

    setSavedFilters((prev) => prev.filter((f) => f.id !== id));
    if (defaultFilterId === id) {
      setDefaultFilterId(null);
    }
  };

  const handleSetDefaultFilter = (filterId) => {
    setDefaultFilterId(filterId);
  };

  const handleUseOnDashboard = (filter) => {
    const keyBase = getKeyBase(user);
    if (keyBase) {
      localStorage.setItem(
        `cv_dashboard_filter_${keyBase}`,
        JSON.stringify(filter)
      );
    }
    setDefaultFilterId(filter.id);
    navigate('/dashboard');
  };


  const handleChangePhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setProfileImage(url);

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(
        'http://localhost:8000/api/profile/upload-avatar/',
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: {
            'X-CSRFToken': csrftoken || '',
          },
        }
      );

      if (!response.ok) {
        const text = await response.text();
        console.error('Upload failed:', response.status, text);
        alert(`Upload failed (${response.status})`);
        return;
      }

      await response.json();
      
      // Refresh user data to get the updated avatar URL
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Error uploading image');
    }
  };


  if (isGuest) {
    return (
      <div className="profile-page">
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => {
            setShowLoginModal(false);
            navigate('/');
          }}
          message="Најавете се за да пристапите до вашиот профил"
        />
        <div className="profile-header">
          <h1>Профил</h1>
          <p className="muted">Најавете се за да пристапите до вашиот профил</p>
        </div>
      </div>
    );
  }


  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>Профил</h1>
        <p className="muted">
          Управувајте со вашите лични информации и зачувани филтри
        </p>
      </div>

      <div className="profile-sections">

        <div className="profile-section">
          <div className="section-header">
            <h2>Лични информации</h2>
            {!isEditing && (
              <button
                className="button button-outline"
                onClick={() => setIsEditing(true)}
              >
                Уреди
              </button>
            )}
          </div>

          <div className="profile-avatar-wrapper">
            <div className="profile-avatar">
              {profileImage ? (
                <img src={profileImage} alt="Profile" />
              ) : (
                <div className="avatar-placeholder">
                  {profileData.name
                    ? profileData.name.charAt(0).toUpperCase()
                    : '?'}
                </div>
              )}
            </div>
            <div className="avatar-actions">
              <button
                type="button"
                className={`button button-outline ${!isEditing ? 'disabled' : ''}`}
                onClick={handleChangePhotoClick}
                disabled={!isEditing}
              >
                Промени слика
              </button>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handlePhotoSelected}
              />
            </div>
          </div>

          {!isEditing ? (
            <div className="profile-view">
              <div className="profile-view-row">
                <span className="label">Корисничко име:</span>
                <span className="value">{user?.username || '-'}</span>
              </div>
              <div className="profile-view-row">
                <span className="label">Име:</span>
                <span className="value">{profileData.name || '-'}</span>
              </div>
              <div className="profile-view-row">
                <span className="label">Е-пошта:</span>
                <span className="value">{profileData.email || '-'}</span>
              </div>
              <div className="profile-view-row">
                <span className="label">Известувања по е-пошта:</span>
                <span className="value">
                  {profileData.notifications ? 'Вклучени' : 'Исклучени'}
                </span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleProfileUpdate} className="profile-form">
              <div className="form-group">
                <label>Корисничко име:</label>
                <input
                  type="text"
                  value={user?.username || ''}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Име:</label>
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) =>
                    setProfileData({ ...profileData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Е-пошта:</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) =>
                    setProfileData({ ...profileData, email: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={profileData.notifications}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        notifications: e.target.checked,
                      })
                    }
                  />
                  Примај известувања по е-пошта
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="button button-primary">
                  Зачувај промени
                </button>
                <button
                  type="button"
                  className="button button-outline"
                  onClick={() => setIsEditing(false)}
                >
                  Откажи
                </button>
              </div>
            </form>
          )}
        </div>


        <div className="profile-section">
          <div className="section-header">
            <h2>Зачувани филтри</h2>
            <button
              className="button button-outline"
              onClick={() => setShowFilterForm(!showFilterForm)}
            >
              + Додади филтер
            </button>
          </div>

          {showFilterForm && (
            <div className="filter-form-card">
              <div className="form-group">
                <label>Име на филтер:</label>
                <input
                  type="text"
                  value={filterForm.name}
                  onChange={(e) =>
                    setFilterForm({ ...filterForm, name: e.target.value })
                  }
                  placeholder="Пр. Висок волумен BTC"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Период:</label>
                  <select
                    value={filterForm.period}
                    onChange={(e) =>
                      setFilterForm({ ...filterForm, period: e.target.value })
                    }
                  >
                    <option value="1D">1 Ден</option>
                    <option value="7D">7 Дена</option>
                    <option value="1M">1 Месец</option>
                    <option value="3M">3 Месеци</option>
                    <option value="1Y">1 Година</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Волумен:</label>
                  <select
                    value={filterForm.volume}
                    onChange={(e) =>
                      setFilterForm({ ...filterForm, volume: e.target.value })
                    }
                  >
                    <option value="all">Сите</option>
                    <option value="high">Висок</option>
                    <option value="low">Низок</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Цена:</label>
                  <select
                    value={filterForm.price}
                    onChange={(e) =>
                      setFilterForm({ ...filterForm, price: e.target.value })
                    }
                  >
                    <option value="all">Сите</option>
                    <option value="above">Над одредена</option>
                    <option value="below">Под одредена</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleSaveFilter}
                >
                  Зачувај
                </button>
                <button
                  type="button"
                  className="button button-outline"
                  onClick={() => {
                    setShowFilterForm(false);
                    setFilterForm({
                      name: '',
                      period: '1M',
                      volume: 'all',
                      price: 'all',
                    });
                  }}
                >
                  Откажи
                </button>
              </div>
            </div>
          )}

          <div className="filters-list">
            {savedFilters.length === 0 ? (
              <div className="empty-state">Нема зачувани филтри</div>
            ) : (
              savedFilters.map((filter) => (
                <div key={filter.id} className="filter-card">
                  <div className="filter-info">
                    <div className="filter-name">
                      {filter.name}{' '}
                      {defaultFilterId === filter.id && (
                        <span className="filter-default-badge">Default</span>
                      )}
                    </div>
                    <div className="filter-details">
                      Период: {filter.period} | Волумен: {filter.volume} | Цена:{' '}
                      {filter.price}
                    </div>
                    <div className="filter-meta">
                      Креирано:{' '}
                      {filter.createdAt
                        ? new Date(filter.createdAt).toLocaleDateString('mk-MK')
                        : ''}
                    </div>
                  </div>
                  <div className="filter-actions">
                    <button
                      className="button button-primary"
                      type="button"
                      onClick={() => handleUseOnDashboard(filter)}
                    >
                      Користи на Dashboard
                    </button>
                    <button
                      className="button button-outline"
                      type="button"
                      onClick={() => handleSetDefaultFilter(filter.id)}
                    >
                      {defaultFilterId === filter.id
                        ? 'Default филтер'
                        : 'Постави како default'}
                    </button>
                    <button
                      className="button button-outline delete-btn"
                      type="button"
                      onClick={() => handleDeleteFilter(filter.id)}
                    >
                      Избриши
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>


        <div className="profile-section">
          <NotificationHistory />
        </div>
      </div>
    </div>
  );
}

export default Profile;
